// covered-by: frontends/carrie/src/utils/queries/__tests__/knowledge.test.tsx
import { type KnowledgeGraphStatus } from "@mg/schemas/src/christo/catalyst";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  archiveKnowledgeRule,
  deleteKnowledgeSource,
  getEnterpriseMemories,
  getKnowledgeActivity,
  getKnowledgeRule,
  getKnowledgeRules,
  getKnowledgeSourceDownload,
  getKnowledgeSourcePresignedUrl,
  getKnowledgeSources,
  promoteKnowledgeRule,
  registerKnowledgeSource,
  rejectKnowledgeRule,
  retryKnowledgeExtraction,
  getKnowledgeGraphStatus,
  requestKnowledgeGraphBuild,
  updateKnowledgeRuleScope,
  updateKnowledgeSourceNote,
  updateKnowledgeSourceScope,
  uploadKnowledgeSourceToS3,
  type GetKnowledgeRulesPayload,
} from "../../services/knowledge";
import { isNil } from "../fp";
import { isSourceInFlight } from "../parsers/knowledge";

/** Poll cadence while a source is processing/extracting. */
const SOURCES_POLL_MS = 4000;

/////
// Sources
/////

export function useKnowledgeSourcesQuery() {
  return useQuery({
    queryKey: ["knowledge-sources"],
    queryFn: () => getKnowledgeSources({ pageSize: 100 }),
    refetchInterval: (query) => {
      const sources = query.state.data?.sources ?? [];
      return sources.some(isSourceInFlight) ? SOURCES_POLL_MS : false;
    },
  });
}

/** Full canonical upload flow: presigned-url -> direct S3 POST -> register.
 * The register body never carries an s3Key — the server reconstructs it. */
export function useUploadKnowledgeSourceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "upload"],
    async mutationFn(file: File) {
      const presigned = await getKnowledgeSourcePresignedUrl({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });
      // parseResponse returns undefined on a malformed payload — surface it
      // as a mutation error instead of crashing mid-upload.
      if (isNil(presigned)) {
        throw new Error("Failed to prepare the upload URL");
      }
      await uploadKnowledgeSourceToS3(presigned.url, presigned.fields, file);
      return registerKnowledgeSource({
        sourceId: presigned.sourceId,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function useUpdateSourceScopeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "scope"],
    mutationFn: updateKnowledgeSourceScope,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function useUpdateSourceNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "note"],
    mutationFn: updateKnowledgeSourceNote,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function useRetryExtractionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "retry-extraction"],
    mutationFn: retryKnowledgeExtraction,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
    },
  });
}

export function useDeleteSourceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "delete"],
    mutationFn: deleteKnowledgeSource,
    onSuccess(_data, sourceId) {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-rules"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
      const previous = queryClient.getQueryData<KnowledgeGraphStatus>([
        "knowledge-graph",
      ]);
      // why: christo rebuilds the graph after a delete archives rules; describe() can still say the previous run is closed, and that read would put the header back on the 30s idle poll.
      // why the guard: a hold is only armable against a REAL pre-delete timestamp. With no cached status there is nothing to hold against; and a cached status whose `builtAt` is null (never built, or built before warhol recorded a time -- christo returns `builtAt: null`) is just as unholdable, because every later read whose `builtAt` is also null would compare equal and force `building: true` for the whole hold window, pinning the header to Building (Build button disabled) for a build nobody asked for and nothing can confirm. No timestamp also means no optimistic Building write.
      if (previous?.builtAt) {
        holdKnowledgeGraphBuilding(previous.builtAt);
        queryClient.setQueryData<KnowledgeGraphStatus>(
          ["knowledge-graph"],
          optimisticallyBuilding(previous),
        );
      }
      queryClient.invalidateQueries({ queryKey: ["knowledge-graph"] });
      // The byte cache is keyed immutable-per-source; a deleted source's
      // bytes must not survive to render in a stale viewer.
      queryClient.removeQueries({
        queryKey: ["knowledge-source-file", sourceId],
      });
    },
  });
}

export type SourceFile = { bytes: ArrayBuffer; mimeType: string };

/**
 * The source FILE BYTES, cached per source. Caching the presigned URL alone
 * still re-downloads the whole document on every viewer mount (pdf.js /
 * SpreadsheetView fetch the full file, and rotating signed URLs defeat the
 * browser HTTP cache). Source bytes are immutable — a re-upload mints a new
 * sourceId — so a cached copy never goes stale; `gcTime` bounds memory and
 * the delete mutation evicts explicitly. Concurrent viewers (inline preview
 * + dialog) dedupe to a single download via the shared query key.
 */
export function useSourceFileQuery(sourceId: string | undefined) {
  return useQuery<SourceFile>({
    queryKey: ["knowledge-source-file", sourceId],
    enabled: !isNil(sourceId),
    staleTime: Infinity,
    gcTime: 15 * 60 * 1000,
    async queryFn() {
      const download = await getKnowledgeSourceDownload(sourceId!);
      if (isNil(download)) {
        throw new Error("Source download URL could not be resolved");
      }
      const resp = await fetch(download.url);
      if (!resp.ok) {
        throw new Error(`Source file fetch failed (${resp.status})`);
      }
      return { bytes: await resp.arrayBuffer(), mimeType: download.mimeType };
    },
  });
}

/////
// Rules
/////

export function useKnowledgeRulesQuery(args: GetKnowledgeRulesPayload) {
  return useQuery({
    queryKey: [
      "knowledge-rules",
      args.status ?? null,
      args.category ?? null,
      args.query ?? null,
      args.sourceId ?? null,
      args.pageSize ?? null,
    ],
    queryFn: () => getKnowledgeRules(args),
  });
}

/** Fetch a single rule by id — resolves a deep-linked citation whose rule
 * isn't on the loaded (paginated) Rules page, so the detail panel can open on
 * the exact cited rule. Disabled until a ruleId is present. */
export function useKnowledgeRuleQuery(ruleId: string | undefined) {
  return useQuery({
    queryKey: ["knowledge-rule", ruleId ?? null],
    enabled: !isNil(ruleId),
    queryFn: () => getKnowledgeRule(ruleId!),
  });
}

/** Imperative load-more (keyset cursor) — later pages append in panel state. */
export function useLoadMoreKnowledgeRulesMutation() {
  return useMutation({
    mutationKey: ["knowledge-rules", "load-more"],
    mutationFn: getKnowledgeRules,
  });
}

function useRuleTransitionMutation(
  key: string,
  mutationFn: (ruleId: string) => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-rules", key],
    mutationFn,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-rules"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function usePromoteRuleMutation() {
  return useRuleTransitionMutation("promote", promoteKnowledgeRule);
}

export function useRejectRuleMutation() {
  return useRuleTransitionMutation("reject", rejectKnowledgeRule);
}

export function useArchiveRuleMutation() {
  return useRuleTransitionMutation("archive", archiveKnowledgeRule);
}

export function useUpdateRuleScopeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-rules", "scope"],
    mutationFn: updateKnowledgeRuleScope,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-rules"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

/////
// Activity
/////

export function useKnowledgeActivityQuery() {
  return useQuery({
    queryKey: ["knowledge-activity"],
    queryFn: () => getKnowledgeActivity({ pageSize: 50 }),
  });
}

export function useLoadMoreKnowledgeActivityMutation() {
  return useMutation({
    mutationKey: ["knowledge-activity", "load-more"],
    mutationFn: getKnowledgeActivity,
  });
}

/////
// Memories (hooks relocated from utils/queries/knowledge-db.tsx — the panel
// and its accumulate-pages contract are unchanged, only the endpoint moved)
/////

export type EnterpriseMemoriesQueryArgs = {
  type?: string;
  query?: string;
  pageSize?: number;
};

/** Page-1 fetch for the Memories tab. Keyed by type + query so changing either
 * refetches from the top; the panel accumulates later pages in local state. */
export function useGetEnterpriseMemoriesQuery(
  args: EnterpriseMemoriesQueryArgs,
) {
  const { type, query, pageSize } = args;
  return useQuery({
    queryKey: [
      "enterprise-memories",
      type ?? null,
      query ?? null,
      pageSize ?? null,
    ],
    queryFn: () => getEnterpriseMemories({ type, query, pageSize }),
  });
}

/** Imperative load-more for the Memories tab. */
export function useLoadMoreEnterpriseMemoriesMutation() {
  return useMutation({
    mutationKey: ["enterprise-memories", "load-more"],
    mutationFn: getEnterpriseMemories,
  });
}

// why: undefined means no hold is armed. The arm point only ever passes a real
// pre-delete timestamp, so a ledger with no `builtAt` can never be held -- a
// null hold would match every later null-`builtAt` read and pin Building.
let graphBuildHoldBuiltAt: string | undefined;
let graphBuildHoldUntil = 0;

// why a second flag instead of comparing `builtAt`: `builtAt` is the ledger of
// the last FINISHED build (christo reads it from `graph_builds`, written on
// finish, while `building` comes from `isWarholGraphBuildRunning` --
// services/christo/app/handlers/catalyst/knowledgeGraph.ts), so it does NOT
// move while a build runs. During a real build it still equals the ledger the
// hold was armed against, which makes it identical to this module's own
// optimistic patch and leaves the rollback below unable to tell the two apart.
// Track the patch by identity instead: true only while the `building: true`
// sitting in the cache is one this module wrote, false the moment christo's own
// status object replaces it.
let graphBuildPatchIsOurs = false;

// why: a signal warhol never answers must not leave the header on Building
// forever -- but the ceiling has to outlast a real rebuild, or the hold expires
// mid-build and the header drops back to the stale "Last built" time (and the
// 30s idle cadence) for exactly the lag this hold exists to cover. The journey
// spec allows a build up to 120s to land
// (qa/rickrubin/tests/specs/cca/knowledge.database.spec.ts), so match it.
const GRAPH_BUILD_HOLD_MS = 120_000;

export function clearKnowledgeGraphBuildHold() {
  graphBuildHoldBuiltAt = undefined;
  graphBuildHoldUntil = 0;
  graphBuildPatchIsOurs = false;
}

export function holdKnowledgeGraphBuilding(previousBuiltAt: string) {
  graphBuildHoldBuiltAt = previousBuiltAt;
  graphBuildHoldUntil = Date.now() + GRAPH_BUILD_HOLD_MS;
  // A freshly armed hold has not patched anything yet.
  graphBuildPatchIsOurs = false;
}

/** This module's own optimistic `building: true`, stamped as ours so the
 * read-error rollback can recognise it without consulting `builtAt`. */
function optimisticallyBuilding(
  status: KnowledgeGraphStatus,
): KnowledgeGraphStatus {
  graphBuildPatchIsOurs = true;
  return { ...status, building: true };
}

/** christo's own status object is now what the cache holds, so whatever
 * `building` it carries is christo's statement and not ours to roll back. */
function forgetOptimisticBuildingPatch() {
  graphBuildPatchIsOurs = false;
}

/** Keep "Building" across a status read that still shows the pre-request ledger. */
export function applyGraphBuildHold(
  status: KnowledgeGraphStatus,
): KnowledgeGraphStatus {
  if (graphBuildHoldBuiltAt === undefined) return status;
  if (Date.now() >= graphBuildHoldUntil) {
    clearKnowledgeGraphBuildHold();
    return status;
  }
  if (status.building) {
    // christo is reporting the build itself now, so the cached Building is its
    // own and the read-error rollback must leave it alone. The hold stays armed
    // (under its ceiling) because `building` can still blink false between
    // warhol stopping and the ledger row landing.
    forgetOptimisticBuildingPatch();
    return status;
  }
  if ((status.builtAt ?? null) !== graphBuildHoldBuiltAt) {
    clearKnowledgeGraphBuildHold();
    return status;
  }
  return optimisticallyBuilding(status);
}

/**
 * Release path for the optimistic `building: true` that does NOT depend on a
 * successful read.
 *
 * why: the 120s ceiling lives in `applyGraphBuildHold`, which only runs after a
 * status read resolves. A read that keeps failing never reaches it, and React
 * Query keeps the last successful `data` across a failed refetch -- that data
 * is the patched object the delete wrote, so the header would assert "Building"
 * forever, `disabled={building}` would keep the one control that could recover
 * it dead, and `refetchInterval` would stay on the 5s build cadence re-issuing
 * the failing read. Enforce the ceiling on the failure path too.
 *
 * Only this module's own optimistic patch is rolled back: a `building: true`
 * christo actually reported is a real build and is left alone. The patch is
 * recognised by `graphBuildPatchIsOurs`, NOT by comparing `builtAt` -- the
 * ledger does not move while a build runs, so a `builtAt` comparison matches a
 * genuine in-flight build just as readily as our own patch and would clear
 * Building mid-build (re-enabling `disabled={building}`), which is the exact
 * regression this path exists to prevent.
 */
function releaseGraphBuildHoldOnReadError(queryClient: QueryClient): void {
  if (graphBuildHoldBuiltAt === undefined) return;
  const rollBackOurPatch = graphBuildPatchIsOurs;
  clearKnowledgeGraphBuildHold();
  if (!rollBackOurPatch) return;
  queryClient.setQueryData<KnowledgeGraphStatus>(
    ["knowledge-graph"],
    (previous) =>
      previous?.building ? { ...previous, building: false } : previous,
  );
}

/** How often the page re-reads the graph's status while a build is running. */
export const KNOWLEDGE_GRAPH_POLL_MS = 5000;

/** ...and while idle. */
export const KNOWLEDGE_GRAPH_IDLE_POLL_MS = 30000;

export function useKnowledgeGraphStatusQuery() {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["knowledge-graph"],
    queryFn: async () => {
      try {
        const status = await getKnowledgeGraphStatus();
        if (status == null) return status;
        return applyGraphBuildHold(status);
      } catch (error) {
        // why here: the hold's ceiling is only consulted on a read that
        // resolves, and this is the path where none ever does. Let go of the
        // optimistic flag so a failing read falls back to "unknown" with the
        // Build button live, instead of a Building header nothing can clear.
        releaseGraphBuildHoldOnReadError(queryClient);
        throw error;
      }
    },
    // why idle polling at all: a reviewer's click and a stored memory start
    // builds this page never asked for, so polling only after its own press
    // left "Last built" stale until a reload -- the memory trigger looked
    // stuck while warhol had finished. Fast while a build runs, slow otherwise.
    refetchInterval: (query) =>
      query.state.data?.building
        ? KNOWLEDGE_GRAPH_POLL_MS
        : KNOWLEDGE_GRAPH_IDLE_POLL_MS,
    // why: the app turns focus refetch off globally; coming back to this tab
    // after saving a memory in a chat is exactly when the time has moved on.
    refetchOnWindowFocus: true,
  });
}

export function useBuildKnowledgeGraphMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-graph", "build"],
    mutationFn: requestKnowledgeGraphBuild,
    onSuccess(status) {
      // why: the same race the delete path holds against, on the entry point a
      // user is most likely to press while watching the header. A status read
      // already in flight when Build was pressed -- or the idle poll that lands
      // before warhol's run reaches RUNNING -- resolves with the pre-build
      // ledger and overwrites this write, dropping the header onto the stale
      // "Last built" time (and the 30s cadence) until the poll after it. Arm
      // the hold against that ledger so the read is patched back to Building.
      const previous = queryClient.getQueryData<KnowledgeGraphStatus>([
        "knowledge-graph",
      ]);
      if (previous?.builtAt) holdKnowledgeGraphBuilding(previous.builtAt);
      // What lands in the cache is christo's own object, not a patch of ours.
      forgetOptimisticBuildingPatch();
      queryClient.setQueryData(["knowledge-graph"], status);
    },
  });
}
